'use client'

import { useLocale, useTranslations } from 'next-intl'
import { Fragment } from 'react'
import { Breadcrumbs } from '@/components/breadcrumbs'
import { Middot } from '@/components/middot'
import { useRestaurantDetailContext } from '@/features/restaurants/restaurant-detail/restaurant-detail-context'
import { RestaurantDetailMobileCta } from '@/features/restaurants/restaurant-detail/restaurant-detail-mobile-cta'
import {
    getRestaurantDetailScrollOffset,
    RestaurantDetailSectionId,
} from '@/features/restaurants/restaurant-detail/utils/restaurant-detail-helpers'
import { useRestaurantOpeningStatus } from '@/features/restaurants/restaurant-detail/utils/use-restaurant-opening-status'
import { RestaurantFlagBadge } from '@/features/restaurants/restaurant-flag-badge'
import { RestaurantPriceRange } from '@/features/restaurants/restaurant-price-range'
import { RestaurantTags } from '@/features/restaurants/restaurant-tags'
import { SvgFilledRatingStar } from '@/icons/svg-filled-rating-star'
import { COLORS } from '@/utils/styles/colors'

const MAX_HEADER_TAGS = 3

export const RestaurantDetailHeader = () => {
    const { restaurant, citySlug, sectionRefs } = useRestaurantDetailContext()

    const t = useTranslations()
    const locale = useLocale()

    const openingStatus = useRestaurantOpeningStatus(restaurant?.openingHours)

    if (!restaurant) return null

    const hasRating = typeof restaurant.avgRating === 'number' && restaurant.avgRating !== 0

    const onRatingClick = () => {
        const reviewsSection = sectionRefs[RestaurantDetailSectionId.REVIEWS]

        if (!reviewsSection) return

        window.scrollTo({
            behavior: 'smooth',
            top: Math.max(reviewsSection.offsetTop - getRestaurantDetailScrollOffset(), 0),
        })
    }

    const hasTags = Boolean(restaurant.tags?.length)

    return (
        <div className="flex flex-col w-full">
            {citySlug && restaurant.city?.name && (
                <Breadcrumbs
                    className="mb-2 md:mb-3"
                    segments={[
                        {
                            label: t('navigation.restaurants'),
                            linkParams: { pattern: '/restaurants', params: undefined },
                        },
                        {
                            label: restaurant.city.name,
                            linkParams: {
                                pattern: '/restaurants/[citySlug]',
                                params: { citySlug },
                            },
                        },
                    ]}
                />
            )}

            <div className="text-display-sm">
                <h1 className="inline">{restaurant.name}</h1>

                {restaurant.flag && (
                    <RestaurantFlagBadge
                        flag={restaurant.flag}
                        className="inline-flex align-middle ml-3 min-h-7 px-3"
                    />
                )}
            </div>

            <div
                className="mt-2 md:mt-3 flex flex-row flex-wrap items-center gap-x-2 gap-y-1 text-p-sm md:text-p-md
                    text-fg-secondary"
            >
                {hasRating && (
                    <button
                        type="button"
                        onClick={onRatingClick}
                        className="flex flex-row items-center gap-x-1.5 cursor-pointer webkit-tap-transparent
                            hover:underline underline-offset-4"
                    >
                        <SvgFilledRatingStar color={COLORS.fg.primary} size={16} />

                        <span className="font-semibold text-fg-primary">
                            {restaurant.avgRating.toLocaleString(locale, {
                                minimumFractionDigits: 1,
                                maximumFractionDigits: 1,
                            })}
                        </span>

                        {Boolean(restaurant.ratingsCount) && (
                            <span>({restaurant.ratingsCount})</span>
                        )}
                    </button>
                )}

                {hasRating && restaurant.priceRange !== null && <Middot />}

                <RestaurantPriceRange
                    priceRange={restaurant.priceRange ?? null}
                    currencySymbol={restaurant.city?.country?.currency?.symbol || '€'}
                    filledColorClassName="text-fg-secondary"
                    emptyColorClassName="text-fg-disabled"
                />

                {(hasRating || restaurant.priceRange !== null) && hasTags && <Middot />}

                {/* Last on the line as the only flexible item: truncates with
                    an ellipsis instead of wrapping the fixed tokens before it */}
                {hasTags && (
                    <RestaurantTags
                        tags={restaurant.tags}
                        maxTags={MAX_HEADER_TAGS}
                        className="min-w-0 flex-1 md:flex-none text-p-sm md:text-p-md"
                    />
                )}

                {/* Two lines on mobile, a single meta row on desktop */}
                <span aria-hidden className="basis-full h-0 md:hidden" />

                {(openingStatus || restaurant.neighborhood) && (
                    <span className="hidden md:inline">
                        <Middot />
                    </span>
                )}

                {openingStatus && (
                    <Fragment>
                        <span className="font-semibold text-fg-primary">
                            {t(
                                openingStatus.isOpen
                                    ? 'restaurants.detail.header.open'
                                    : 'restaurants.detail.header.closed'
                            )}
                        </span>

                        {openingStatus.isOpen && openingStatus.closingTime && (
                            <Fragment>
                                <Middot />
                                <span>
                                    {t('restaurants.detail.header.closesAt', {
                                        time: openingStatus.closingTime,
                                    })}
                                </span>
                            </Fragment>
                        )}

                        {!openingStatus.isOpen && openingStatus.nextOpeningTime && (
                            <Fragment>
                                <Middot />
                                <span>
                                    {t('restaurants.detail.header.opensAt', {
                                        time: openingStatus.nextOpeningTime,
                                    })}
                                </span>
                            </Fragment>
                        )}
                    </Fragment>
                )}

                {openingStatus && restaurant.neighborhood && <Middot />}

                {restaurant.neighborhood && <span>{restaurant.neighborhood}</span>}
            </div>

            <RestaurantDetailMobileCta className="mt-3" />
        </div>
    )
}
